import { API, ApiResponse, handleApiResponse, handleApiError } from './config';

export interface ProfileData {
  user_id: number;
  first_name: string;
  last_name: string;
  mobile_phone: string;
  email?: string;
  id_card?: string;
}

export interface StoreData {
  name: string;
  address: string;
  reference?: string;
  zone?: string;
  storeImage?: string;
  latitude?: string;
  longitude?: string;
}

export interface CommissionRule {
  id: number;
  operator: string;
  logo?: string;
  /** "0" = porcentaje, "1" = monto fijo */
  type: string;
  /** valor de la comisión */
  value: string;
}

/**
 * Servicio para la gestión del perfil del usuario
 */
export const profileService = {
  /** Obtener los datos del perfil del usuario */
  getProfile: async (): Promise<ApiResponse<ProfileData>> => {
    try {
      const response = await API.post('/get-profile', {});
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Actualizar datos del perfil (first_name, last_name, email, id_card) */
  editProfile: async (data: Partial<Pick<ProfileData, 'first_name' | 'last_name' | 'email' | 'id_card'>>): Promise<ApiResponse<ProfileData>> => {
    try {
      const response = await API.post('/edit-profile', data);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Obtener los datos de la tienda */
  getStore: async (): Promise<ApiResponse<StoreData>> => {
    try {
      const response = await API.post('/get-store', {});
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Actualizar datos de la tienda (name, address, latitude, longitude, reference) */
  editStore: async (data: Pick<StoreData, 'name' | 'address' | 'reference' | 'latitude' | 'longitude'>): Promise<ApiResponse<StoreData>> => {
    try {
      const response = await API.post('/edit-store', data);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Actualizar logo de la tienda — multipart/form-data */
  updateStoreImage: async (file: File): Promise<ApiResponse<string>> => {
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await API.post('/update-store-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Obtener reglas de comisión — type: 0=Recargas, 1=Paquetes, 2=Facturas, 3=Seguros */
  getCommissionRules: async (type: 0 | 1 | 2 | 3): Promise<ApiResponse<CommissionRule[]>> => {
    try {
      const response = await API.post('/get-commission-rules', { type: String(type) });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Enviar código de verificación para cambio de contraseña */
  sendChangePasswordCode: async (): Promise<ApiResponse<any>> => {
    try {
      const response = await API.post('/send-change-password-code', {});
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Validar el código recibido */
  validateChangePasswordCode: async (code: string): Promise<ApiResponse<any>> => {
    try {
      const response = await API.post('/validate-change-password-code', { code });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /** Cambiar la contraseña con el código validado */
  changePassword: async (data: { code: string; password: string; password_confirmation: string }): Promise<ApiResponse<any>> => {
    try {
      const response = await API.post('/change-password', data);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};
